import { useState } from 'react';
import * as stylex from '@stylexjs/stylex';
import { useMutation } from '@apollo/client/react';
import { FaApple } from 'react-icons/fa';
import type { ResultOf, VariablesOf } from '@graphql-typed-document-node/core';
import { authErrorMessage, type AuthSession } from '~utils/auth';
import { loadAppleSdk } from './sdk';
import { LOGIN_WITH_APPLE } from './operations';
import { styles } from './styles';
type Props = {
  onLogin: (session: AuthSession) => void;
  onError: (message: string | null) => void;
};

// Web port of widgets/continue_with/apple_button.dart: black, white label,
// brand icon on the left, self-contained sign-in logic. Apple permits custom
// buttons that follow its style guide, so this one matches the app. The
// popup's return URL is the hosting page — every page that mounts this must
// be registered as a return URL on the Services ID in the Apple portal.
export function ContinueWithApple({
  onLogin,
  onError
}: Props) {
  const [loading, setLoading] = useState(false);
  const [loginWithApple] = useMutation(LOGIN_WITH_APPLE);
  if (!process.env.APPLE_SERVICES_ID) return null;
  const deps: SignInDeps = {
    loading,
    setLoading,
    loginWithApple,
    onLogin,
    onError
  };
  return <button type="button" {...stylex.props(styles.button)} onClick={() => signIn(deps)} disabled={loading}>
      <FaApple size={20} {...stylex.props(styles.icon)} />
      {loading ? 'Signing in…' : 'Continue with Apple'}
    </button>;
}
type LoginWithAppleFn = (options: {
  variables: VariablesOf<typeof LOGIN_WITH_APPLE>;
}) => Promise<{
  data?: ResultOf<typeof LOGIN_WITH_APPLE> | null;
}>;
type SignInDeps = Props & {
  loading: boolean;
  setLoading: (loading: boolean) => void;
  loginWithApple: LoginWithAppleFn;
};
const signIn = async ({
  loading,
  setLoading,
  loginWithApple,
  onLogin,
  onError
}: SignInDeps) => {
  if (loading) return;
  onError(null);
  setLoading(true);
  try {
    const apple = await loadAppleSdk();
    apple.auth.init({
      clientId: process.env.APPLE_SERVICES_ID,
      scope: 'name email',
      redirectURI: `${window.location.origin}${window.location.pathname}`,
      usePopup: true
    });
    const response = await apple.auth.signIn();
    // Apple returns the name only on first authorization; the API takes it
    // as one flat string.
    const name = response.user?.name;
    const fullName = [name?.firstName, name?.lastName].filter(Boolean).join(' ');
    const result = await loginWithApple({
      variables: {
        input: {
          idToken: response.authorization.id_token,
          fullName: fullName || undefined
        }
      }
    });
    if (result.data) onLogin(result.data.loginWithApple);
  } catch (err: unknown) {
    // Closing Apple's popup rejects with { error: 'popup_closed_by_user' }.
    if ((err as {
      error?: string;
    }).error !== 'popup_closed_by_user') {
      onError(authErrorMessage(err));
    }
  } finally {
    setLoading(false);
  }
};